import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errText = (e, fb) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fb);

function Shell({ title, sub, children, tid }) {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid={tid}>
      <div className="w-full max-w-md rounded-3xl border-2 bg-white p-7 shadow-xl" style={{ borderColor: "#E4E7E5" }}>
        <KeyRound className="w-8 h-8" style={{ color: "#F45118" }} />
        <h1 className="mt-3 font-display text-3xl font-bold" style={{ color: "#00604A" }}>{title}</h1>
        <p className="text-sm mt-1" style={{ color: "#667085" }}>{sub}</p>
        {children}
      </div>
    </div>
  );
}

const inputCls = "mt-4 w-full rounded-xl border-2 px-3 py-3 text-sm outline-none focus:border-[#00604A]";
const btnCls = "mt-4 w-full rounded-full py-3 font-bold text-white shadow-lg disabled:opacity-60 transition-transform hover:-translate-y-0.5";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await axios.post(`${API}/auth/forgot-password`, { email }); setDone(r.data.message); }
    catch (err) { toast.error(errText(err, "Please enter a valid email")); }
    finally { setBusy(false); }
  };
  return (
    <Shell tid="forgot-password-page" title="Forgot password?" sub="Enter your registered email. Our team will send a secure reset link to your WhatsApp (valid 1 hour).">
      {done ? (
        <div data-testid="forgot-done" className="mt-5 rounded-2xl p-4 text-sm flex gap-2" style={{ background: "#F0FDF4", color: "#14532D" }}>
          <MessageCircle className="w-5 h-5 shrink-0" /> {done}
        </div>
      ) : (
        <form onSubmit={submit}>
          <input required type="email" data-testid="forgot-email-input" className={inputCls} placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button disabled={busy} data-testid="forgot-submit" className={btnCls} style={{ background: "#F45118" }}>{busy ? "Sending…" : "Request reset link"}</button>
        </form>
      )}
      <Link to="/login" className="mt-4 inline-block text-xs font-bold" style={{ color: "#00604A" }} data-testid="forgot-back-login">← Back to login</Link>
    </Shell>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/reset-password`, { token, password: pw });
      toast.success("Password updated — please log in");
      nav(r.data.account_type === "partner" ? "/partner/login" : "/login");
    } catch (err) { toast.error(errText(err, "Could not reset password")); }
    finally { setBusy(false); }
  };
  if (!token) return <Shell tid="reset-password-page" title="Link missing" sub="Open the full reset link you received on WhatsApp." />;
  return (
    <Shell tid="reset-password-page" title="Set a new password" sub="Choose at least 6 characters.">
      <form onSubmit={submit}>
        <input required minLength={6} type="password" data-testid="reset-password-input" className={inputCls} placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} />
        <input required minLength={6} type="password" data-testid="reset-password-confirm" className={inputCls} placeholder="Confirm password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
        <button disabled={busy} data-testid="reset-submit" className={btnCls} style={{ background: "#00604A" }}>{busy ? "Saving…" : "Update password"}</button>
      </form>
    </Shell>
  );
}
